import { createColumnHelper } from '@tanstack/react-table';
import { intlFormat } from 'date-fns';
import { DownloadArrow } from '#assets/ReactSvg.ts';
import { IconLink } from '#components/atoms/link/icon-link/IconLink.tsx';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { BillingDetails } from '#dn-types/billing.ts';
import { UnreachableCodeError } from '#util/assert-unreachable.ts';

type BillingHistoryItem = NonNullable<BillingDetails['invoices']>[number];

type Props = {
  data: NonNullable<BillingDetails['invoices']>;
};

const columnHelper = createColumnHelper<BillingHistoryItem>();

const columns = [
  columnHelper.accessor('status', {
    header: 'Status',
    cell(info) {
      const value = info.getValue();
      switch (value) {
        case 'paid':
          return <Pill variant="success">Paid</Pill>;
        case 'open':
          return <Pill>Awaiting payment</Pill>;
        case 'void':
          return <Pill variant="alert">Cancelled</Pill>;
        default:
          throw new UnreachableCodeError(value, 'unexpected invoice status');
      }
    },
  }),
  columnHelper.accessor('date', {
    header: 'Issue Date',
    cell(info) {
      const value = info.getValue();

      return `${intlFormat(new Date(value), { timeZone: 'utc' })} UTC`;
    },
  }),
  columnHelper.accessor('description', { header: 'Description', meta: { truncate: true } }),
  columnHelper.accessor('amount', {
    header: 'Amount Due',
    cell(info) {
      const value = info.getValue();

      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value / 100);
    },
    meta: { align: 'right' },
  }),
  columnHelper.accessor('downloadURL', {
    header: '',
    size: 35,
    cell(info) {
      const value = info.getValue();
      if (!value) {
        throw new Error('expected a download url');
      }
      return (
        <div className="flex justify-end">
          <IconLink title="Download invoice" download={true} to={value} reloadDocument={true}>
            <DownloadArrow />
          </IconLink>
        </div>
      );
    },
  }),
];

export function BillingHistoryList({ data }: Props) {
  return (
    <div className="scroll-x">
      <Table data={data.slice(0, 12)} columns={columns} />
    </div>
  );
}
